import { useCallback, useEffect, useState } from 'react';
import type { FC } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

import { useParentalControls } from '../ParentalControl/shared/useParentalControls';
import { ActivatedParentalControls } from '../ParentalControl/States/ActivatedParentalControls/ActivatedParentalControls';
import { parentalControlsHelper } from '../../helpers/parentalControls';
import { usePlayerEvent } from '../utils/usePlayerEvent';
import { usePlayerContext } from '../../player/PlayerContext';

import * as styles from './withParentalControls.module.scss';

export const withParentalControls =
  <T extends {}>(Component: FC<T>) =>
  (props: T & { asset: SvpAssetAdditional }) => {
    const { asset } = props;
    const { canDisplayVideo } = useParentalControls(asset);
    const [isUnlocked, setIsUnlocked] = useState(canDisplayVideo);
    const [isPopupVisible, setPopupVisible] = useState(false);

    const { stopPlayback, startPlayback } = usePlayerContext();

    const handlePlay = useCallback(() => {
      // stop playback and show popup on play attempt
      if (!isUnlocked) {
        stopPlayback();
        setPopupVisible(true);
      }
    }, [isUnlocked, stopPlayback]);

    usePlayerEvent('play', handlePlay);

    const onUnlockClick = useCallback(() => setIsUnlocked(true), []);

    useEffect(() => {
      if (canDisplayVideo) {
        return;
      }

      // hide popup on unlock
      if (isUnlocked) {
        setPopupVisible(false);
        startPlayback();
      }
    }, [canDisplayVideo, startPlayback, isUnlocked]);

    useEffect(() => {
      // hide popup upon navigation
      setPopupVisible(false);
      setIsUnlocked(canDisplayVideo);
    }, [asset]);

    const code = parentalControlsHelper.getPin();

    return (
      <div className={styles.container}>
        {isPopupVisible && (
          <>
            <div className={styles.backdrop}></div>
            <div className={styles.controls}>
              <ActivatedParentalControls
                code={code}
                handleUnlock={onUnlockClick}
              />
            </div>
          </>
        )}
        <Component {...props} />
      </div>
    );
  };
